import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import MatrixQuestionSection from './MatrixQuestionSection';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiChevronDown, FiChevronUp, FiCheckCircle, FiAlertCircle, FiInfo } = FiIcons;

function ScoreCard({ area, responses, delay = 0, forceExpanded = false }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const maxScore = 35;
  const percentage = (area.score / maxScore) * 100;

  useEffect(() => {
    if (forceExpanded) setIsExpanded(true);
    else setIsExpanded(false);
  }, [forceExpanded]);

  if (!area.performance) return null;

  return (
    <motion.div 
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.5 }}
      whileHover={{ y: -5 }}
      className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100 flex flex-col h-full"
    >
      {/* Header Area */}
      <div className={`h-2 bg-gradient-to-r ${area.color}`}></div>
      
      <div className="p-8 pb-4 flex items-start justify-between">
        <div className="flex gap-4">
          <div className="w-14 h-14 bg-gray-50 rounded-2xl flex items-center justify-center text-3xl shadow-inner">
            {area.icon}
          </div>
          <div>
            <h3 className="text-xl font-bold text-gray-800 leading-tight">{area.title}</h3>
            <p className="text-sm text-gray-500 mt-1">{area.description}</p>
          </div>
        </div>
        <div className={`text-sm font-black px-3 py-1 rounded-lg ${area.performance.bgColor} ${area.performance.color} uppercase tracking-tighter`}>
          {area.performance.level}
        </div>
      </div>

      {/* Score Visual */}
      <div className="px-8 mb-6">
        <div className="flex items-end justify-between mb-2">
          <div className="flex items-baseline gap-1">
            <span className="text-4xl font-black text-gray-800">{area.score}</span>
            <span className="text-gray-400 font-bold">/35</span>
          </div>
          <span className="text-sm font-bold text-gray-500">{percentage.toFixed(0)}% Maturity</span>
        </div>
        <div className="w-full bg-gray-100 rounded-full h-4 relative overflow-hidden">
          <motion.div 
            className={`absolute top-0 left-0 h-full bg-gradient-to-r ${area.color}`}
            initial={{ width: 0 }}
            animate={{ width: `${percentage}%` }}
            transition={{ duration: 1, delay: delay + 0.5 }}
          />
        </div>
      </div>

      {/* Meaningful Context */}
      <div className="px-8 pb-8 flex-grow">
        <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
          <div className="flex items-center gap-2 mb-3 text-gray-700 font-bold text-sm">
            <SafeIcon icon={FiInfo} className="text-blue-500" />
            What this indicates:
          </div>
          <ul className="space-y-3">
            {percentage >= 70 ? (
              <li className="flex items-start gap-3 text-sm text-gray-600">
                <SafeIcon icon={FiCheckCircle} className="text-green-500 mt-0.5 shrink-0" />
                <span>Your organization demonstrates clear competence in this domain with standardized processes and high team alignment.</span>
              </li>
            ) : (
              <li className="flex items-start gap-3 text-sm text-gray-600">
                <SafeIcon icon={FiAlertCircle} className="text-amber-500 mt-0.5 shrink-0" />
                <span>Responses suggest inconsistencies that may be causing operational friction or slowing down strategic execution.</span>
              </li>
            )}
            <li className="flex items-start gap-3 text-sm text-gray-600">
              <div className="w-1.5 h-1.5 rounded-full bg-blue-400 mt-1.5 shrink-0" />
              <span>{percentage >= 90 ? "Focus on innovation and external benchmarking." : "Prioritize internal communication and role clarity."}</span>
            </li>
          </ul>
        </div>
      </div>

      {/* Expansion Toggle */}
      <div className="bg-gray-50/50 border-t border-gray-100 px-4 py-2">
        <button 
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full py-2 flex items-center justify-center gap-2 text-sm font-bold text-blue-600 hover:text-blue-700 transition-colors"
        >
          {isExpanded ? 'Hide Details' : 'View Raw Data'}
          <SafeIcon icon={isExpanded ? FiChevronUp : FiChevronDown} />
        </button>
      </div>

      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden bg-white border-t border-gray-100"
          >
            <div className="p-4">
              <MatrixQuestionSection area={area} responses={responses} readOnly={true} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export default ScoreCard;